import type { FavoriteRecord, Locale, PersistedState, SearchItem, SearchRecord, ThemeMode, ViewedRecord } from '../types';

const STORAGE_KEY = 'apple-media-explorer:v1';

export const DEFAULT_STATE: PersistedState = {
  version: 1,
  theme: 'system',
  locale: 'zh',
  defaultCountry: 'US',
  discoverPresetId: 'music-songs',
  discoverCountry: 'US',
  discoverLimit: 25,
  cardSize: 180,
  comparisonCountries: ['US', 'AU', 'JP', 'GB', 'CN'],
  favorites: [],
  recentSearches: [],
  recentViewed: []
};

export function loadPersistedState(storage: Storage = localStorage): PersistedState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (parsed.version !== 1) return DEFAULT_STATE;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      theme: coerceTheme(parsed.theme),
      locale: coerceLocale(parsed.locale),
      discoverPresetId: typeof parsed.discoverPresetId === 'string' ? parsed.discoverPresetId : DEFAULT_STATE.discoverPresetId,
      discoverCountry: typeof parsed.discoverCountry === 'string' ? parsed.discoverCountry : DEFAULT_STATE.discoverCountry,
      discoverLimit: typeof parsed.discoverLimit === 'number' ? parsed.discoverLimit : DEFAULT_STATE.discoverLimit,
      cardSize: coerceCardSize(parsed.cardSize),
      favorites: Array.isArray(parsed.favorites) ? parsed.favorites : [],
      recentSearches: Array.isArray(parsed.recentSearches) ? parsed.recentSearches : [],
      recentViewed: Array.isArray(parsed.recentViewed) ? parsed.recentViewed : []
    };
  } catch {
    return DEFAULT_STATE;
  }
}

export function savePersistedState(state: PersistedState, storage: Storage = localStorage) {
  storage.setItem(STORAGE_KEY, JSON.stringify({ ...state, version: 1 }));
}

export function upsertFavorite(favorites: FavoriteRecord[], item: SearchItem) {
  const existing = favorites.find((favorite) => favorite.id === item.id);
  if (existing) return favorites.filter((favorite) => favorite.id !== item.id);
  return [{ id: item.id, item, savedAt: new Date().toISOString() }, ...favorites].slice(0, 200);
}

export function addRecentSearch(records: SearchRecord[], record: Omit<SearchRecord, 'id' | 'searchedAt'>) {
  const id = `${record.term}:${record.media}:${record.entity ?? ''}:${record.country}`.toLowerCase();
  return [
    { ...record, id, searchedAt: new Date().toISOString() },
    ...records.filter((item) => item.id !== id)
  ].slice(0, 20);
}

export function addRecentViewed(records: ViewedRecord[], item: SearchItem) {
  return [
    { id: item.id, item, viewedAt: new Date().toISOString() },
    ...records.filter((record) => record.id !== item.id)
  ].slice(0, 30);
}

function coerceTheme(value: unknown): ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system' ? value : DEFAULT_STATE.theme;
}

function coerceLocale(value: unknown): Locale {
  return value === 'en' || value === 'zh' ? value : DEFAULT_STATE.locale;
}

function coerceCardSize(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_STATE.cardSize;
  return Math.min(260, Math.max(150, Math.round(value)));
}
